import type { LaunchLocale } from "../../../locales/src/locales";
export const postPurchaseScreens = [
  "B09",
  "B10",
  "B11",
  "B12",
  "B13",
  "B14",
  "B15",
  "S06",
  "S07",
  "S09",
  "S11",
  "O01",
] as const;
export type PostPurchaseScreen = (typeof postPurchaseScreens)[number];
type Labels = {
  titles: readonly string[];
  context: readonly string[];
  facts: string;
  items: string;
  totals: string;
  changes: string;
  before: string;
  after: string;
  messages: string;
  original: string;
  attribution: string;
  timeline: string;
  receipt: string;
  reference: string;
  version: string;
  committed: string;
  settlement: string;
  pending: string;
  empty: string;
  noReceipt: string;
};
export const postPurchaseCopy = {
  en: {
    titles: [
      "Purchase receipt",
      "Order history",
      "Custom quote and proof",
      "Your conversation",
      "Return request",
      "Order help and case",
      "Purchase review",
      "Production and custom work",
      "Shipment and tracking",
      "Returns and refunds",
      "Customer conversation",
      "Support workspace",
    ],
    context: [
      "The items you bought and what’s happened with your order so far.",
      "Choose an order to see its details.",
      "Check every change to the terms before you confirm below.",
      "Sending a message doesn’t change your order or approve a payment.",
      "Check the item you bought, the shop’s return policy and your return options.",
      "Asking for help or sending evidence doesn’t approve a refund on its own.",
      "If this purchase can be reviewed, you’ll see the option here.",
      "Make what the customer ordered, following the latest production instructions.",
      "A shipping update alone doesn’t confirm delivery or payment.",
      "Check each original amount and the current refund or access status.",
      "Sending a message doesn’t approve any change to the purchase.",
      "You’ll only see the evidence and actions you have permission to use.",
    ],
    facts: "Details",
    items: "Items and order details",
    totals: "Amounts",
    changes: "Changed terms",
    before: "Previous",
    after: "Current",
    messages: "Messages",
    original: "Original text",
    attribution: "Translation details",
    timeline: "Order activity",
    receipt: "Receipt",
    reference: "Reference",
    version: "Version",
    committed: "Recorded at",
    settlement:
      "This receipt shows what happened with your request. It doesn’t confirm that the money has reached the bank.",
    pending:
      "Your previous request is still being processed. Please wait for it to finish before trying something else. Pending doesn’t mean the payment went through.",
    empty: "Nothing here yet.",
    noReceipt:
      "There’s no receipt yet. An activity update alone doesn’t mean the purchase is complete.",
  },
  es: {
    titles: [
      "Recibo de compra",
      "Historial de pedidos",
      "Presupuesto y prueba personalizados",
      "Tu conversación",
      "Solicitud de devolución",
      "Ayuda y caso del pedido",
      "Reseña de compra",
      "Producción y trabajo personalizado",
      "Envío y seguimiento",
      "Devoluciones y reembolsos",
      "Conversación con el cliente",
      "Espacio de soporte",
    ],
    context: [
      "Los artículos que compraste y lo que ha pasado con tu pedido hasta ahora.",
      "Elige un pedido para ver sus detalles.",
      "Revisa cada cambio en las condiciones antes de confirmar abajo.",
      "Enviar un mensaje no cambia tu pedido ni aprueba un pago.",
      "Revisa el artículo que compraste, la política de devoluciones de la tienda y tus opciones de devolución.",
      "Pedir ayuda o enviar pruebas no aprueba por sí solo un reembolso.",
      "Si puedes reseñar esta compra, verás la opción aquí.",
      "Elabora lo que pidió el cliente siguiendo las instrucciones de producción más recientes.",
      "Una actualización de envío por sí sola no confirma la entrega ni el pago.",
      "Revisa cada importe original y el estado actual del reembolso o del acceso.",
      "Enviar un mensaje no aprueba ningún cambio en la compra.",
      "Solo verás las pruebas y acciones que tienes permiso para usar.",
    ],
    facts: "Detalles",
    items: "Artículos y detalles del pedido",
    totals: "Importes",
    changes: "Condiciones modificadas",
    before: "Anterior",
    after: "Actual",
    messages: "Mensajes",
    original: "Texto original",
    attribution: "Detalles de la traducción",
    timeline: "Actividad del pedido",
    receipt: "Recibo",
    reference: "Referencia",
    version: "Versión",
    committed: "Registrado el",
    settlement:
      "Este recibo muestra qué ocurrió con tu solicitud. No confirma que el dinero haya llegado al banco.",
    pending:
      "Tu solicitud anterior todavía se está procesando. Espera a que termine antes de intentar otra cosa. Pendiente no significa que el pago se haya realizado.",
    empty: "Todavía no hay nada aquí.",
    noReceipt:
      "Todavía no hay recibo. Una actualización de actividad por sí sola no significa que la compra esté completa.",
  },
  fr: {
    titles: [
      "Reçu d’achat",
      "Historique des commandes",
      "Devis et épreuve personnalisés",
      "Votre conversation",
      "Demande de retour",
      "Aide et dossier de commande",
      "Avis sur l’achat",
      "Production et travail personnalisé",
      "Expédition et suivi",
      "Retours et remboursements",
      "Conversation client",
      "Espace d’assistance",
    ],
    context: [
      "Les articles que vous avez achetés et le suivi de votre commande jusqu’ici.",
      "Choisissez une commande pour voir ses détails.",
      "Vérifiez chaque modification des conditions avant de confirmer ci-dessous.",
      "Envoyer un message ne modifie pas votre commande et n’approuve aucun paiement.",
      "Vérifiez l’article acheté, la politique de retour de la boutique et vos options de retour.",
      "Demander de l’aide ou envoyer des preuves n’approuve pas un remboursement à lui seul.",
      "Si vous pouvez donner votre avis sur cet achat, l’option apparaîtra ici.",
      "Réalisez ce que le client a commandé en suivant les dernières instructions de production.",
      "Une mise à jour d’expédition ne confirme à elle seule ni la livraison ni le paiement.",
      "Vérifiez chaque montant d’origine et l’état actuel du remboursement ou de l’accès.",
      "Envoyer un message n’approuve aucune modification de l’achat.",
      "Vous ne voyez que les preuves et les actions que vous êtes autorisé à utiliser.",
    ],
    facts: "Détails",
    items: "Articles et détails de la commande",
    totals: "Montants",
    changes: "Conditions modifiées",
    before: "Précédent",
    after: "Actuel",
    messages: "Messages",
    original: "Texte original",
    attribution: "Détails de la traduction",
    timeline: "Activité de la commande",
    receipt: "Reçu",
    reference: "Référence",
    version: "Version",
    committed: "Enregistré le",
    settlement:
      "Ce reçu indique ce qu’il est advenu de votre demande. Il ne confirme pas que l’argent est arrivé à la banque.",
    pending:
      "Votre demande précédente est encore en cours de traitement. Attendez qu’elle se termine avant de faire autre chose. « En attente » ne signifie pas que le paiement a abouti.",
    empty: "Il n’y a encore rien ici.",
    noReceipt:
      "Il n’y a pas encore de reçu. Une mise à jour d’activité ne signifie pas à elle seule que l’achat est terminé.",
  },
  de: {
    titles: [
      "Kaufbeleg",
      "Bestellverlauf",
      "Individuelles Angebot und Freigabevorlage",
      "Deine Unterhaltung",
      "Rückgabeanfrage",
      "Bestellhilfe und Fall",
      "Kaufbewertung",
      "Produktion und Sonderanfertigung",
      "Versand und Sendungsverfolgung",
      "Rückgaben und Erstattungen",
      "Kundengespräch",
      "Support-Arbeitsbereich",
    ],
    context: [
      "Die Artikel, die Sie gekauft haben, und was bisher mit Ihrer Bestellung passiert ist.",
      "Wählen Sie eine Bestellung, um die Details zu sehen.",
      "Prüfen Sie jede Änderung der Bedingungen, bevor Sie unten bestätigen.",
      "Eine Nachricht ändert Ihre Bestellung nicht und genehmigt keine Zahlung.",
      "Prüfen Sie den gekauften Artikel, die Rückgaberichtlinie des Shops und Ihre Rückgabeoptionen.",
      "Eine Hilfeanfrage oder eingereichte Nachweise genehmigen allein noch keine Erstattung.",
      "Wenn Sie diesen Kauf bewerten können, sehen Sie die Option hier.",
      "Fertigen Sie, was der Kunde bestellt hat, nach den aktuellen Produktionsanweisungen.",
      "Eine Versandmeldung allein bestätigt weder die Zustellung noch die Zahlung.",
      "Prüfen Sie jeden ursprünglichen Betrag und den aktuellen Erstattungs- oder Zugriffsstatus.",
      "Eine Nachricht genehmigt keine Änderung am Kauf.",
      "Sie sehen nur die Nachweise und Aktionen, die Sie nutzen dürfen.",
    ],
    facts: "Details",
    items: "Artikel und Bestelldetails",
    totals: "Beträge",
    changes: "Geänderte Bedingungen",
    before: "Vorher",
    after: "Aktuell",
    messages: "Nachrichten",
    original: "Originaltext",
    attribution: "Angaben zur Übersetzung",
    timeline: "Bestellverlauf",
    receipt: "Beleg",
    reference: "Referenz",
    version: "Version",
    committed: "Gespeichert am",
    settlement:
      "Dieser Beleg zeigt, was mit Ihrer Anfrage passiert ist. Er bestätigt nicht, dass das Geld bei der Bank angekommen ist.",
    pending:
      "Ihre vorherige Anfrage wird noch bearbeitet. Warten Sie bitte, bis sie abgeschlossen ist, bevor Sie etwas anderes versuchen. „Ausstehend“ heißt nicht, dass die Zahlung erfolgt ist.",
    empty: "Hier gibt es noch nichts.",
    noReceipt:
      "Es gibt noch keinen Beleg. Eine Aktivitätsmeldung allein bedeutet nicht, dass der Kauf abgeschlossen ist.",
  },
  ja: {
    titles: [
      "購入の領収書",
      "注文履歴",
      "カスタム見積もりと校正",
      "会話",
      "返品申請",
      "注文の相談と案件",
      "購入レビュー",
      "製作とカスタム作業",
      "発送と追跡",
      "返品と返金",
      "お客様との会話",
      "サポート作業画面",
    ],
    context: [
      "購入した商品と、これまでの注文の状況です。",
      "注文を選ぶと詳細が表示されます。",
      "下で確定する前に、条件の変更点をすべてご確認ください。",
      "メッセージを送っても、注文が変わったり支払いが承認されたりすることはありません。",
      "購入した商品、ショップの返品ポリシー、返品の選択肢をご確認ください。",
      "サポートへの相談や資料の提出だけでは、返金は承認されません。",
      "この購入にレビューを書ける場合は、ここに表示されます。",
      "最新の制作指示に沿って、お客様が注文したものを制作してください。",
      "発送の更新だけでは、配達や支払いの完了は確認できません。",
      "元の金額と、現在の返金・アクセスの状況をご確認ください。",
      "メッセージを送っても、購入内容の変更は承認されません。",
      "利用が許可された資料と操作だけが表示されます。",
    ],
    facts: "詳細",
    items: "商品と注文の詳細",
    totals: "金額",
    changes: "条件の変更",
    before: "変更前",
    after: "現在",
    messages: "メッセージ",
    original: "原文",
    attribution: "翻訳の詳細",
    timeline: "注文のアクティビティ",
    receipt: "領収書",
    reference: "参照番号",
    version: "バージョン",
    committed: "記録日時",
    settlement:
      "この領収書はリクエストの結果を示すものです。銀行への着金を確認するものではありません。",
    pending:
      "前回のリクエストはまだ処理中です。完了してから別の操作をお試しください。「保留中」は支払い完了を意味しません。",
    empty: "まだ何もありません。",
    noReceipt:
      "領収書はまだありません。アクティビティの更新だけでは購入完了とはなりません。",
  },
} satisfies Record<LaunchLocale, Labels>;
